/**
 * src/hooks/useRemoteViewer.ts
 *
 * Reactive hook managing remote trip manifest downloading, AbortController lifecycle,
 * CORS and network diagnostics, and URL hash/query route synchronization.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  parseShareRoute,
  ParsedShareRoute,
  hydrateTripData,
  HydratedTripData,
  validateManifestSchema,
  resolveAssetUrl,
} from '../services/remoteHydration';
import { PhotoRecord } from '../types/domain';

export interface RemoteViewerState {
  isShareMode: boolean;
  isLoading: boolean;
  error: string | null;
  hydratedData: HydratedTripData | null;
  manifestUrl: string | null;
  retry: () => void;
  exitReadOnly: () => void;
}

export function useRemoteViewer(): RemoteViewerState {
  const [route, setRoute] = useState<ParsedShareRoute>(() => parseShareRoute());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hydratedData, setHydratedData] = useState<HydratedTripData | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Synchronize route state when URL hash or popstate changes
  useEffect(() => {
    const handleUrlChange = () => {
      const newRoute = parseShareRoute();
      setRoute(newRoute);
    };

    window.addEventListener('hashchange', handleUrlChange);
    window.addEventListener('popstate', handleUrlChange);

    return () => {
      window.removeEventListener('hashchange', handleUrlChange);
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []);

  const loadManifest = useCallback(async (url: string) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setError(null);

    // 1. Device Offline pre-check
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setIsLoading(false);
      setError('Dispositivo offline: impossibile scaricare il viaggio remoto senza connessione.');
      return;
    }

    try {
      // 15-second network timeout guard
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Manifest di viaggio non trovato sul server (404 Not Found): ${url}`);
        } else if (response.status === 403) {
          throw new Error(
            'Accesso negato al manifest di viaggio (403 Forbidden). Verifica i permessi CORS o la configurazione pubblica del bucket R2.'
          );
        } else {
          throw new Error(`Errore server durante il download del manifest: HTTP ${response.status} ${response.statusText}`);
        }
      }

      let json: unknown;
      try {
        json = await response.json();
      } catch {
        throw new Error('Il file scaricato non è un formato JSON valido.');
      }

      // 2. Validate Schema
      const manifest = validateManifestSchema(json);

      // If manifest specifies external trip or photos files, resolve and fetch them if needed
      let externalPhotos: PhotoRecord[] | undefined = undefined;
      const manifestFiles = (manifest as any).files;

      if (!Array.isArray((manifest as any).photos) && manifestFiles?.photos) {
        const photosUrl = resolveAssetUrl(manifestFiles.photos, url);
        try {
          const photosRes = await fetch(photosUrl, { signal: controller.signal });
          if (photosRes.ok) {
            externalPhotos = await photosRes.json();
          }
        } catch {
          // If fetch fails, proceed with photos in manifest if any
        }
      }

      // If trip details are external
      if (manifestFiles?.trip && (!manifest.trip || !manifest.trip.name)) {
        const tripUrl = resolveAssetUrl(manifestFiles.trip, url);
        try {
          const tripRes = await fetch(tripUrl, { signal: controller.signal });
          if (tripRes.ok) {
            manifest.trip = await tripRes.json();
          }
        } catch {}
      }

      // 3. Hydrate strictly in-memory
      const hydrated = hydrateTripData(manifest, url, externalPhotos);
      setHydratedData(hydrated);
      setIsLoading(false);
    } catch (err: any) {
      if (err.name === 'AbortError') {
        return; // Request was aborted cleanly
      }
      setIsLoading(false);
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        setError(
          'Impossibile contattare il server remoto. Verifica che il server supporti CORS (Access-Control-Allow-Origin: *) o che la connessione internet sia attiva.'
        );
      } else {
        setError(err.message || 'Si è verificato un errore sconosciuto durante il caricamento del viaggio.');
      }
    }
  }, []);

  // Fetch when route changes and is in share mode
  useEffect(() => {
    if (route.isShareMode && route.manifestUrl) {
      loadManifest(route.manifestUrl);
    } else {
      setHydratedData(null);
      setError(route.error || null);
      setIsLoading(false);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [route, loadManifest]);

  const retry = useCallback(() => {
    if (route.manifestUrl) {
      loadManifest(route.manifestUrl);
    }
  }, [route.manifestUrl, loadManifest]);

  const exitReadOnly = useCallback(() => {
    if (typeof window !== 'undefined') {
      // Clear hash to return to local workspace
      window.location.hash = '';
      if (window.history?.pushState) {
        const cleanUrl = window.location.pathname;
        window.history.pushState(null, '', cleanUrl);
      }
    }
    setRoute({ isShareMode: false, manifestUrl: null, rawParam: null, paramKey: null });
    setHydratedData(null);
    setError(null);
  }, []);

  return {
    isShareMode: route.isShareMode,
    isLoading,
    error,
    hydratedData,
    manifestUrl: route.manifestUrl,
    retry,
    exitReadOnly,
  };
}
